/* eslint-disable import/no-named-as-default-member */
import React, { Fragment, useEffect } from 'react';
import PropTypes from 'prop-types';
import Metrics from '@spotahome/soyuz-metrics';
import { Mediator } from '@spotahome/soyuz-mediator';
import { trans } from '@spotahome/soyuz/client';
import {
  Alarm as AlarmIcon,
  Button,
  useModalHandle
} from '@spotahome/ui-library';

import ButtonWithIcon from '@components/ButtonWithIcon';

import { addAlert } from '@components/AlertModal/mutations';
import AlertModalWrapper from '@components/AlertModal/index';

import { showSuccessToast } from '@alerts/components/AlertToast';

import { TOKEN_EXPIRED } from '@common/channels';
import SoyuzLocalStorage from '@utils/soyuzLocalStorage';

const LOCAL_STORAGE_ALERT_DATA = 'alertData';

const parseBudget = (budget = '') => {
  const splitBudget = budget.split('-');

  return {
    minPrice: splitBudget[0],
    maxPrice: splitBudget[1]
  };
};

const getAlertFromFilters = (cityId, filters) => ({
  cityId,
  moveIn: filters['move-in'],
  moveOut: filters['move-out'],
  propertyTypes: filters['type[]'],
  cityAreaIds: filters['areaId[]'],
  ...parseBudget(filters.budget)
});

const getAlertDataFromStorage = () => {
  const data = SoyuzLocalStorage.extractItem(LOCAL_STORAGE_ALERT_DATA);
  try {
    return JSON.parse(data);
  } catch (e) {
    return null;
  }
};

const createAlert = async alertData => {
  try {
    await addAlert(alertData);
    Metrics.ga.sendEvent(
      'My alerts modal',
      'alert creation success',
      'alert creation success'
    );
    showSuccessToast();
    SoyuzLocalStorage.removeItem(LOCAL_STORAGE_ALERT_DATA);
  } catch (error) {
    console.error(error);
  }
};

const AlertButtonWithoutLogin = React.memo(
  ({ hasButtonVariant, cityId, filters, isUserLogged, allCities, onClick }) => {
    const { isModalOpen, handleModalOpen, handleModalClose } = useModalHandle();

    useEffect(() => {
      const alertData = getAlertDataFromStorage();
      if (alertData && isUserLogged) {
        createAlert(alertData);
      }
    }, [isUserLogged]);

    const getInitialAlertValues = () => getAlertFromFilters(cityId, filters);

    const handleOpen = () => {
      handleModalOpen();
      onClick();
    };

    const handleConfirm = async alertData => {
      Metrics.ga.sendEvent(
        'My alerts modal',
        'create alert button',
        'click button'
      );

      if (isUserLogged) {
        await createAlert(alertData);
      } else {
        SoyuzLocalStorage.setItem(
          LOCAL_STORAGE_ALERT_DATA,
          JSON.stringify(alertData)
        );
        Mediator.publish(TOKEN_EXPIRED);
      }
      handleModalClose();
    };

    return (
      <Fragment>
        {hasButtonVariant ? (
          <Button
            onClick={handleOpen}
            alt
            className="filter-header__results-button filter-header__results-button--alerts--variant"
          >
            {trans('search.alert.button.save-my-search')}
          </Button>
        ) : (
          <ButtonWithIcon
            className="filter-header__results-button filter-header__results-button--alerts ga-search-form-my-alerts"
            title={trans('search.filter_header.alerts')}
            // eslint-disable-next-line react/jsx-handler-names
            handleOnClick={handleOpen}
            color="secondary"
          >
            {() => <AlarmIcon size="normal" theme="default" />}
          </ButtonWithIcon>
        )}

        <AlertModalWrapper
          alert={getInitialAlertValues(cityId, filters)}
          cities={allCities}
          onConfirm={handleConfirm}
          onClose={handleModalClose}
          isModalOpen={isModalOpen}
        />
      </Fragment>
    );
  }
);

AlertButtonWithoutLogin.propTypes = {
  cityId: PropTypes.string.isRequired,
  filters: PropTypes.shape({
    'bathrooms[]': PropTypes.arrayOf(PropTypes.string),
    bed: PropTypes.string,
    budget: PropTypes.string,
    'features[]': PropTypes.arrayOf(PropTypes.string),
    'move-in': PropTypes.string,
    'move-out': PropTypes.string,
    poi: PropTypes.string,
    'topology[]': PropTypes.arrayOf(PropTypes.string),
    'type[]': PropTypes.arrayOf(PropTypes.string)
  }).isRequired,
  allCities: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      currencyIsoCode: PropTypes.string
    })
  ).isRequired,
  isUserLogged: PropTypes.bool,
  hasButtonVariant: PropTypes.bool,
  onClick: PropTypes.func
};

AlertButtonWithoutLogin.defaultProps = {
  isUserLogged: false,
  hasButtonVariant: false,
  onClick: () => {}
};

export default AlertButtonWithoutLogin;
